import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Close } from '@mui/icons-material';
import {
  convertTimeRangeToTs,
  isRelativeTimeRange,
} from 'src/features/standardDesign/components/DateTimeRangePicker';
import EXECUTE_REPORT from '../../graphql/mutation/triggerDataExtraction';
import { useMutation } from '@apollo/client';
import { setSnackbar } from 'src/slices/components';
import { useDispatch } from 'src/store';
import { IFormSubmitReportCreation } from './FormFirstStepReportCreation';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const CreateReportDialog = (props: {
  open: boolean;
  formReportCreation: IFormSubmitReportCreation | undefined;
  onClose: () => void;
}) => {
  const dispatch = useDispatch();
  const { _ } = useLingui();
  const { open, onClose, formReportCreation } = props;
  const [triggerDataExtraction, { loading }] = useMutation(EXECUTE_REPORT, {
    onCompleted: () => {
      onClose();
      dispatch(
        setSnackbar({
          open: true,
          message: _(
            msg`La solicitud de reporte a sido enviada correctamente. Pronto recibirá el reporte en los emails seleccionados.`,
          ),
          severity: 'success',
        }),
      );
    },
    onError: (error) => {
      console.error(error);
      let errorMessage = _(
        msg`Hubo un error en la solicitud del reporte, por favor intente nuevamente`,
      );

      if (error.graphQLErrors?.some((err) => err.extensions?.code === 'REPORT_DATA_LIMIT_PDF')) {
        errorMessage = _(
          msg`Error al generar reporte en PDF: El periodo de tiempo y la cantidad de métricas seleccionadas exceden el límite permitido para generar reportes en formato PDF. Si necesitas un reporte más extenso, por favor contacta al equipo de soporte para ayudarte con una solución personalizada.`,
        );
      }

      if (error.graphQLErrors?.some((err) => err.extensions?.code === 'REPORT_DATA_LIMIT_EXCEL')) {
        errorMessage = _(
          msg`Error al generar reporte en Excel: El volumen de datos solicitado supera el límite permitido para la generación de reportes en formato Excel.Te recomendamos reducir el rango de fechas o la cantidad de métricas. Si necesitas un archivo más completo, puedes comunicarte con soporte para recibir asistencia.`,
        );
      }

      dispatch(
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        }),
      );
    },
  });

  const handleSubmit = () => {
    if (!formReportCreation) {
      console.error('confirmDialogParams is undefined');
      return;
    }
    const { request, meanWindow, timezone, date, createExcel, createPdf, emails } =
      formReportCreation;

    const config = {
      createPdf: createPdf,
      createExcel: createExcel,
      emails: emails,
    };

    const requestConfig = {
      meanWindow: meanWindow,
      timezone: timezone,
      relativeTime: null as string | null,
      start: null as number | null,
      stop: null as number | null,
    };

    const isRelative = isRelativeTimeRange(date);
    if (isRelative) {
      requestConfig.relativeTime = date.start;
    } else {
      const { start, stop } = convertTimeRangeToTs(date);
      requestConfig.start = start;
      requestConfig.stop = stop;
    }

    triggerDataExtraction({
      variables: {
        input: {
          metrics: request.metrics.map((metric) => ({
            name: metric.metricName,
            unit: metric.unit,
          })),
          devices: request.deviceIds,
          emails,
          createExcel,
          createPdf,
          ...requestConfig,
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
          <Typography variant="h6">
            <Trans>Crear Reporte Instantáneo</Trans>
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography>
          <Trans>¿Está seguro que quiere crear un nuevo reporte?</Trans>
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="info">
          <Trans>Cancelar</Trans>
        </Button>
        <Button disabled={loading} onClick={handleSubmit} variant="contained" color="success">
          <Trans>Confirmar</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateReportDialog };
